/**
 * 句子辅助: 接口返回的句子 + 高亮区间 → 视图模型.
 *
 * 高亮区间由 `GET /courses/{id}/highlights` 按用户词表动态匹配得到(见后端
 * app/services/word_match.py), 前端按字符偏移把句子切成若干片段渲染(不用 v-html)。
 * 收藏页等没有高亮上下文的场景不传区间, 整句即为一个普通片段。
 */

import { formatTime } from './format'
import type { Sentence, SentenceHighlight } from '../types/api'

export interface SentencePart {
  text: string
  highlight: boolean
  /** 命中词典时的词条 id, 点击弹词卡用 */
  wordId?: number
  /** 命中词条所属难度, 用于行内配色 */
  difficulty?: string
}

export interface SentenceView {
  id: number
  marker: string
  text: string
  translation: string
  start: number
  end: number
  parts: SentencePart[]
}

/** 按区间把整句切成「普通 / 高亮」交替的片段; 区间已按偏移升序且互不重叠. */
function buildParts(text: string, spans: SentenceHighlight[]): SentencePart[] {
  if (spans.length === 0) return text ? [{ text, highlight: false }] : []

  const parts: SentencePart[] = []
  let cursor = 0
  for (const span of spans) {
    if (span.start < cursor || span.end > text.length) continue
    if (span.start > cursor) {
      parts.push({ text: text.slice(cursor, span.start), highlight: false })
    }
    parts.push({
      text: text.slice(span.start, span.end),
      highlight: true,
      wordId: span.word_id,
      difficulty: span.difficulty,
    })
    cursor = span.end
  }
  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), highlight: false })
  }
  return parts
}

function groupBySentence(spans: SentenceHighlight[]): Map<number, SentenceHighlight[]> {
  const grouped = new Map<number, SentenceHighlight[]>()
  for (const span of spans) {
    const list = grouped.get(span.sentence_id)
    if (list) {
      list.push(span)
    } else {
      grouped.set(span.sentence_id, [span])
    }
  }
  // 后端已按句子顺序返回; 这里保证区间升序, 便于线性切分
  for (const list of grouped.values()) list.sort((a, b) => a.start - b.start)
  return grouped
}

export function toSentenceViews(
  sentences: Sentence[],
  highlights: SentenceHighlight[] = [],
): SentenceView[] {
  const grouped = groupBySentence(highlights)
  return sentences.map((sentence, position) => ({
    id: sentence.id,
    marker: sentence.marker || `#${String(position + 1).padStart(2, '0')}`,
    text: sentence.text,
    translation: sentence.translation,
    start: sentence.start_seconds,
    end: sentence.end_seconds,
    parts: buildParts(sentence.text, grouped.get(sentence.id) ?? []),
  }))
}

/** 句子时间区间, 例如 00:10 - 00:20 */
export function rangeLabel(sentence: { start: number; end: number }): string {
  return `${formatTime(sentence.start)} - ${formatTime(sentence.end)}`
}

/** 秒 → mm:ss 标签, 非法值回退为 00:00 */
export function durationLabelOf(seconds: number): string {
  return formatTime(Number.isFinite(seconds) && seconds > 0 ? seconds : 0)
}
